/**
 * Base API fetch client with automatic auth header injection and silent
 * refresh on 401 (spec §14 token model; invariant 7 - token never in storage).
 *
 * The access token lives in React state (AuthContext). On 401 the client
 * retries POST /auth/refresh - the httpOnly Secure cookie carries the refresh
 * token; no JS can read it. On a REJECTED refresh the auth state is cleared and
 * the caller receives an ApiError(401); a refresh that fails in TRANSPORT
 * throws NetworkError and never clears auth - a network blip must not log
 * anyone out.
 */

import { errorMessage, type ApiValidationIssue } from '../lib/error-labels.ts';
import { ApiError, NetworkError } from './errors.ts';

export { ApiError, NetworkError };

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

export interface TokenStore {
  getToken: () => string | null;
  onRefresh: (data: RefreshResponse) => void;
  onAuthFailure: () => void;
  onMfaRequired?: (upgrade: boolean) => void;
}

export interface RefreshResponse {
  accessToken: string;
  id: string;
  email: string;
  displayName: string;
  role: string;
}

export async function parseError(res: Response): Promise<ApiError> {
  let code = 'UNKNOWN';
  let message: string | undefined;
  let issues: ApiValidationIssue[] | undefined;
  try {
    const body = (await res.json()) as {
      error?: string;
      message?: string;
      issues?: ApiValidationIssue[];
    };
    code = body.error ?? code;
    message = body.message; // server-provided copy always wins
    issues = body.issues;
  } catch { /* empty body */ }
  // Domain routes send { error: CODE } only - translate centrally so no
  // screen ever shows raw status text like "Forbidden" (audit finding B2).
  return new ApiError(res.status, code, message ?? errorMessage(code, res.status, issues));
}

/**
 * Single-flight refresh. The refresh token rotates on every use and the old one
 * is revoked (token-reuse family revocation, Suite 2). If two requests 401 at
 * once - or the on-load bootstrap races a query - they MUST share one refresh
 * call, or the second uses an already-revoked token and the whole family is
 * revoked. All refresh paths (apiFetch + AuthProvider bootstrap) funnel here. A 5xx response is thrown as NetworkError, not resolved to null — it means the gateway/API failed, not that there is no session.
 *
 * No Content-Type / body: the token is in the httpOnly cookie; an
 * application/json header with an empty body trips Fastify's empty-body guard
 * (400 FST_ERR_CTP_EMPTY_JSON_BODY).
 */
let inflightRefresh: Promise<RefreshResponse | null> | null = null;

/**
 * The ONE place a raw fetch rejection is classified into NetworkError - offline,
 * DNS failure, CORS, or the injected timeout. Uploads are base64-in-JSON up to
 * ~35 MB and cannot finish in 60s on slow links, so a large string body gets 5
 * minutes instead.
 *
 * `timeoutMs` widens that window WITHOUT taking the caller-signal path: a caller
 * signal means "I may cancel", and an abort from it cannot be reported as a
 * timeout, so passing one to buy time mislabels the timeout as unreachable.
 */
export async function classifiedFetch(
  input: string,
  init?: RequestInit,
  opts?: { timeoutMs?: number },
): Promise<Response> {
  const hasCallerSignal = init?.signal != null;
  const timeoutMs =
    opts?.timeoutMs ??
    (typeof init?.body === 'string' && init.body.length > 2_000_000 ? 300_000 : 60_000);
  try {
    return await fetch(input, {
      ...init,
      signal: init?.signal ?? AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    const timeout =
      !hasCallerSignal && err instanceof DOMException &&
      (err.name === 'TimeoutError' || err.name === 'AbortError');
    throw new NetworkError(timeout);
  }
}

export function silentRefresh(): Promise<RefreshResponse | null> {
  if (!inflightRefresh) {
    inflightRefresh = classifiedFetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (r) => {
        if (r.ok) return (await r.json()) as RefreshResponse;
        if (r.status >= 500) throw new NetworkError(false);
        return null;
      })
      .finally(() => {
        inflightRefresh = null;
      });
  }
  return inflightRefresh;
}

async function doFetch(
  path: string,
  init: RequestInit,
  token: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers);
  // Only declare a JSON body when one is actually sent. A Content-Type of
  // application/json with an empty body trips Fastify's empty-body guard
  // (400 FST_ERR_CTP_EMPTY_JSON_BODY) on bodyless POSTs (e.g. dpa/accept,
  // notifications mark-all-read).
  if (init.body !== undefined && init.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return classifiedFetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include', // carry the httpOnly refresh cookie on every request
  });
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit,
  store: TokenStore,
  opts?: { suppressMfaRedirect?: boolean },
): Promise<T> {
  let res = await doFetch(path, init, store.getToken());

  if (res.status === 401) {
    // Parse the body first to determine whether this is a token expiry or an
    // application-level rejection. Consuming the body here is safe: we either
    // throw immediately or replace res with the retry response below.
    const firstError = await parseError(res);

    // Application-level 401s that are NOT token expiry: throw immediately so the
    // caller handles them without consuming the refresh token (v7.6 bug 3). A
    // silent refresh would rotate the token without fixing the error, and if the
    // cookie Path is wrong it causes a spurious navigate-to-login.
    //
    // MFA_INVALID_CODE: valid session, wrong TOTP code - user must re-enter.
    if (firstError.code === 'MFA_INVALID_CODE') {
      throw firstError;
    }
    // MFA_REQUIRED: valid session, MFA step not yet completed. Redirect to /mfa
    // unless the caller opts out - break-glass requires an MFA-verified session
    // and surfaces the denial inline via suppressMfaRedirect (cluster 4).
    // MFA_UPGRADE_REQUIRED goes straight to /mfa/setup: through /mfa it cost two emailed codes.
    if (firstError.code === 'MFA_REQUIRED' || firstError.code === 'MFA_UPGRADE_REQUIRED') {
      if (!opts?.suppressMfaRedirect) store.onMfaRequired?.(firstError.code === 'MFA_UPGRADE_REQUIRED');
      throw firstError;
    }

    // True token expiry (UNAUTHORIZED) or unknown 401: single-flight silent refresh.
    const refreshData = await silentRefresh();
    if (refreshData) {
      store.onRefresh(refreshData);
      res = await doFetch(path, init, refreshData.accessToken);
    } else {
      store.onAuthFailure();
      throw new ApiError(401, 'UNAUTHORIZED', errorMessage('UNAUTHORIZED', 401));
    }
  }

  if (!res.ok) {
    const error = await parseError(res);
    // Non-401 MFA_REQUIRED (e.g. break-glass returns it outside the token-expiry
    // path): redirect to /mfa for session/login MFA, but let the caller surface it
    // inline via suppressMfaRedirect (cluster 4).
    if (
      (error.code === 'MFA_REQUIRED' || error.code === 'MFA_UPGRADE_REQUIRED') &&
      !opts?.suppressMfaRedirect
    ) {
      store.onMfaRequired?.(error.code === 'MFA_UPGRADE_REQUIRED');
    }
    throw error;
  }
  // 204 No Content: return empty object cast to T
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}
